/**
 * Login session of this browser tab.
 *
 * - The short-lived ACCESS token lives only in memory (a page reload drops it; XSS cannot read it
 *   from storage because it is never stored).
 * - The long-lived REFRESH token is an httpOnly cookie set by the backend; JavaScript never sees
 *   it. `refreshAccessToken()` asks the backend to exchange it for a new access token.
 * - Tabs of one browser share the cookie, so refreshes are serialized across tabs with the Web
 *   Locks API: only one tab rotates the cookie at a time (the backend also tolerates concurrent
 *   refreshes for a few seconds — see ADR-0002 in the platform repo).
 */

import { isEnvelope, unwrap } from "./envelope";

export type SessionStatus = "unknown" | "authenticated" | "anonymous";

interface SessionState {
  status: SessionStatus;
  accessToken: string | null;
  accountId: string | null;
}

const REFRESH_URL = "/api/v1/auth/refresh";
const REFRESH_LOCK = "ishonch-auth-refresh";

let state: SessionState = { status: "unknown", accessToken: null, accountId: null };
const listeners = new Set<() => void>();
let inFlightRefresh: Promise<boolean> | null = null;
/** Another way to get a session when the refresh cookie gives none (the Telegram Mini App launch data). */
let signInFallback: (() => Promise<boolean>) | null = null;

/**
 * Registers a sign-in to try when the refresh cookie fails. Inside Telegram's web client the store runs in a
 * cross-site frame, where the SameSite=Strict refresh cookie is never sent; a linked customer then gets a new
 * session from the Mini App launch data instead of being signed out mid-flow. It must call only auth endpoints.
 */
export function setSignInFallback(fallback: (() => Promise<boolean>) | null): void {
  signInFallback = fallback;
}

async function fallbackOrSignOut(): Promise<boolean> {
  if (signInFallback) {
    try {
      if (await signInFallback()) return true;
    } catch {
      // the fallback refused as well: signed out below
    }
  }
  session.signOut();
  return false;
}

function setState(next: SessionState) {
  state = next;
  listeners.forEach((listener) => {
    listener();
  });
}

export const session = {
  getState: (): SessionState => state,

  getAccessToken: (): string | null => state.accessToken,

  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  /** Called after a successful login or refresh. */
  signIn: (accessToken: string, accountId: string) => {
    setState({ status: "authenticated", accessToken, accountId });
  },

  /** Forget the session locally (the caller is responsible for calling /logout first). */
  signOut: () => {
    setState({ status: "anonymous", accessToken: null, accountId: null });
  },
};

/**
 * Gets a new access token using the refresh cookie. Concurrent callers in this tab share one
 * request; other tabs wait for the lock. Resolves to false when the user is not logged in.
 */
export function refreshAccessToken(): Promise<boolean> {
  inFlightRefresh ??= withCrossTabLock(doRefresh).finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

async function doRefresh(): Promise<boolean> {
  try {
    const response = await fetch(new URL(REFRESH_URL, window.location.origin), {
      method: "POST",
      credentials: "same-origin",
      signal: AbortSignal.timeout(15_000),
    });
    if (!response.ok) return await fallbackOrSignOut();
    const body: unknown = await response.json();
    if (!isEnvelope(body) || !body.success) return await fallbackOrSignOut();
    const tokens = unwrap(body) as { as_access_token: string; as_account_id: string };
    session.signIn(tokens.as_access_token, tokens.as_account_id);
    return true;
  } catch {
    // Network failure: do not log the user out because of a flaky connection.
    if (state.status === "unknown") session.signOut();
    return false;
  }
}

function withCrossTabLock<T>(task: () => Promise<T>): Promise<T> {
  // Web Locks are available in all current browsers (secure contexts); tests and very old
  // browsers fall back to the in-tab de-duplication above.
  if (typeof navigator !== "undefined" && "locks" in navigator) {
    return navigator.locks.request(REFRESH_LOCK, task);
  }
  return task();
}
